import React, { useState } from 'react';
import {
  Button,
  Col,
  Row,
  Box,
  VerticalSpacing,
  Text,
  Input,
} from '../../components/Boneyard';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import { Formik, Form } from 'formik';
import FormField from '../../components/FormField';
import { IATA_FIELD, PASSWORD_FIELD, AGENT_NAME_FIELD } from './partsName';
import { getValidationSchema } from '../../utils/validateLoginPage';
import { Checkbox } from '@g4/boneyard/lib/Checkbox/Checkbox';
import TAForgotPasswordPopUp from './TAForgotPasswordPopUp';
import EARN_ICON from '../../images/earn-icon-blue.svg';
import QUALITY_ICON from '../../images/quality-icons-blue.svg';
import LOWFARE_ICON from '../../images/lowfares-icon-blue.svg';
import BUILD_ICON from '../../images/build-icon-blue.svg';
import {
  withApplicationQuery,
  useTranslations,
} from '../../graphql/components/ApplicationQueryProvider';
import FieldErrorMessage from '../../components/FieldErrorMessage';
import LOGIN_PAGE_TRANSLATIONS_QUERY from './taLoginPageTranslations.query.graphql.js';
import LOGIN_MUTATION from '../../graphql/components/authenticationProvider/login.mutation.graphql.js';
import Markdown from '../../components/Markdown';

const StyledRow = styled(Row)`
  padding: 10px;
`;

const StyledSection = styled(Box)`
  font-size: 13px;
  padding-top: 60px;
  padding-bottom: 90px;
  justify-content: space-around;
`;

const StyledBox = styled(Box)`
  background-color: ${props => props?.theme.colors.common.light};
  border: 2px solid ${props => props?.theme.colors.common.light};
  height: 600px;
  padding: 12px;
`;

const StyledButton = styled(Button)`
  width: 180px;
  float: right;
`;

const TitleBox = styled(Box)`
  padding: 15px;
  align-items: center;
  color: ${props => props?.theme.colors.common.peacockBlue};
`;

const StyledListItems = styled.li`
  display: flex;
  background-size: 26px 36px;
  background-repeat: no-repeat;
  background-position: left top;
  list-style: none;
  padding-left: 40px;
  line-height: 35px;
  align-items: flex-start;
  &.earn {
    background-image: url(${EARN_ICON});
  }
  &.quality {
    background-image: url(${QUALITY_ICON});
  }
  &.lowfare {
    background-image: url(${LOWFARE_ICON});
  }
  &.buildcust {
    background-image: url(${BUILD_ICON});
  }
`;

const StyledBoxContent = styled(Box)`
  font-size: 15px;
  line-height: 30px;
`;

const HorizontalRule = styled.hr`
  border-bottom: 1px solid gray;
  height: 0.5px;
  opacity: 0.5;
`;

const UnorderedList = styled.ul`
  line-height: 2rem;
`;

const TaLoginPage = () => {
  const [open, setOpen] = useState(false);
  const [openEnroll, setOpenEnroll] = useState(false);
  const {
    travelAgentLoginPage,
    common,
    loginSignUpForm,
    travelAgentErrorMessage,
  } = useTranslations();

  const openPopup = () => {
    setOpen(!open);
  };

  const openNewPage = () => {
    setOpenEnroll(!openEnroll);
  };

  const [login] = useMutation(LOGIN_MUTATION);

  const initialFormValues = {
    [IATA_FIELD]: '',
    [PASSWORD_FIELD]: '',
    [AGENT_NAME_FIELD]: '',
  };

  const submitHandler = (values, { setFieldError }) => {
    login({
      variables: {
        userName: values[IATA_FIELD],
        password: values[PASSWORD_FIELD],
        agentName: values[AGENT_NAME_FIELD],
        rememberMe: isChecked,
        application: 'TRAVEL_AGENT',
      },
    })
      .then(res => {
        if (res.data.login.token) {
          window.location.href = '/';
        } else {
          setFieldError(
            IATA_FIELD,
            travelAgentErrorMessage.incorrectLoginMessage
          );
        }
      })
      .catch(err => {
        err.length &&
          setFieldError(
            IATA_FIELD,
            travelAgentErrorMessage.unknownLoginMessage
          );
      });
  };

  const [isChecked, setIsChecked] = useState(false);

  const handleToggle = () => {
    setIsChecked(!isChecked);
  };

  return (
    <StyledSection d="flex">
      <StyledBox w={700}>
        <TitleBox>
          <Text size={30} weight={750}>
            {travelAgentLoginPage.taTitleLabel}
          </Text>
        </TitleBox>
        <VerticalSpacing size={2} />
        <HorizontalRule />
        <Formik
          initialValues={initialFormValues}
          onSubmit={submitHandler}
          validationSchema={getValidationSchema(travelAgentErrorMessage)}
          validateOnChange={true}
        >
          {({ values, touched, handleChange, errors }) => {
            return (
              <Form onChange={handleChange}>
                <VerticalSpacing size={5} />
                <StyledRow>
                  <Col span={22}>
                    <FormField
                      component={Input}
                      name={IATA_FIELD}
                      type="text"
                      placeholder={travelAgentLoginPage.taIATAPlaceholderLabel}
                      label={travelAgentLoginPage.taIATALabel}
                      value={values[IATA_FIELD]}
                      hasError={errors[IATA_FIELD] && touched[IATA_FIELD]}
                      isRequired
                    />
                    <FieldErrorMessage fieldName={IATA_FIELD} />
                  </Col>
                </StyledRow>
                <VerticalSpacing size={1} />

                <StyledRow>
                  <Col span={22}>
                    <FormField
                      component={Input}
                      name={PASSWORD_FIELD}
                      type="password"
                      placeholder={travelAgentLoginPage.taPasswordLabel}
                      label={travelAgentLoginPage.taPasswordPlaceholderLabel}
                      value={values[PASSWORD_FIELD]}
                      hasError={
                        errors[PASSWORD_FIELD] && touched[PASSWORD_FIELD]
                      }
                      isRequired
                    />
                    <FieldErrorMessage fieldName={PASSWORD_FIELD} />
                  </Col>
                </StyledRow>
                <VerticalSpacing size={1} />

                <StyledRow>
                  <Col span={22}>
                    <FormField
                      component={Input}
                      name={AGENT_NAME_FIELD}
                      type="text"
                      placeholder={
                        travelAgentLoginPage.taAgencyNamePlaceholderLabel
                      }
                      label={travelAgentLoginPage.taAgencyNameLabel}
                      value={values[AGENT_NAME_FIELD]}
                      hasError={
                        errors[AGENT_NAME_FIELD] && touched[AGENT_NAME_FIELD]
                      }
                      isRequired
                    />
                    <FieldErrorMessage fieldName={AGENT_NAME_FIELD} />
                  </Col>
                </StyledRow>
                <VerticalSpacing size={1} />
                <StyledRow>
                  <Col span={22}>
                    <Box d="inline-block">
                      <span
                        style={{ textDecoration: 'underline', color: 'blue' }}
                        onClick={openPopup}
                      >
                        {common.forgotPasswordTitle}?
                      </span>
                      <TAForgotPasswordPopUp
                        openPopup={openPopup}
                        open={open}
                      />
                      <VerticalSpacing size={3} />
                      <Checkbox value={isChecked} onChange={handleToggle}>
                        <Text lh={1.7}>
                          {loginSignUpForm.loginSignupRememberMe}
                        </Text>
                      </Checkbox>
                    </Box>
                    <StyledButton htmlType="submit">
                      {travelAgentLoginPage.taloginLoginButtonLabel}
                    </StyledButton>
                  </Col>
                </StyledRow>
              </Form>
            );
          }}
        </Formik>
      </StyledBox>

      <StyledBox w={400}>
        <TitleBox>
          <Text size={30} weight={600}>
            {travelAgentLoginPage.taPartneringLabel}
          </Text>
        </TitleBox>
        <VerticalSpacing size={5} />
        <StyledBoxContent pl={3}>
          <Markdown source={travelAgentLoginPage.taBookingEarnContent} />
          <VerticalSpacing size={3} />
          <Markdown
            source={travelAgentLoginPage.taBookingEarnList}
            customElements={{
              ul: UnorderedList,
              li: StyledListItems,
            }}
          />
        </StyledBoxContent>
        <VerticalSpacing size={6} />
        <Button
          as="a"
          href="/ta-enrollment"
          target="_blank"
          onClick={openNewPage}
          style={{ float: 'right', width: '180px' }}
        >
          {travelAgentLoginPage.taEnrollButtonLabel}
        </Button>
      </StyledBox>
    </StyledSection>
  );
};

export default withApplicationQuery(LOGIN_PAGE_TRANSLATIONS_QUERY, TaLoginPage);
